import React, { useCallback, useEffect, useState } from 'react';
import { Portal } from '../Portal/Portal';
import styles from './ScrollableDrawer.module.scss';

export interface ScrollableDrawerProps {
  // We send the closeDrawer function to the children to allow them to close the drawer with animation
  children: ({ closeDrawer }: { closeDrawer: () => void }) => React.ReactNode;
  onClickOutside?: () => void;
}

export const ScrollableDrawer = ({ children, onClickOutside = () => {} }: ScrollableDrawerProps) => {
  const [drawerClasses, setDrawerClasses] = useState('');
  const [backdropClasses, setBackdropClasses] = useState('');

  const closeDrawer = useCallback(() => {
    setDrawerClasses(styles.isClosing);
    setBackdropClasses(styles.isFadingOut);
    // Wait for the animation to complete before unmounting the component.
    setTimeout(() => {
      onClickOutside();
    }, 240); // Matches the CSS transition duration.
  }, [onClickOutside]);

  // useEffect to animate the drawer on mount (entry animation).
  useEffect(() => {
    // A small delay allows the browser to 'see' the initial position before transitioning.
    const timeoutId = setTimeout(() => {
      setDrawerClasses(styles.isOpen);
      setBackdropClasses('');
    }, 10);
    return () => clearTimeout(timeoutId);
  }, []);

  // useEffect to control the body's overflow to prevent scrolling the page behind the drawer.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <Portal>
      <div className={`${styles.listingsBackdrop} ${backdropClasses}`} onClick={closeDrawer}></div>
      <div className={`${styles.drawer} ${drawerClasses}`}>{children({ closeDrawer })}</div>
    </Portal>
  );
};
